import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTenant } from "../lib/tenant-context";
import { tenantFetch } from "../lib/api-client";

// Types for Sponsored Ads
interface SponsoredCampaign {
  _key: string;
  brand_id: string;
  brand_name: string;
  name: string;
  status: "active" | "paused" | "deleted";
  created_at: string;
  budget: {
    daily_limit: number;
    monthly_limit: number;
    spent_today: number;
    spent_month: number;
    total_spent: number;
  };
  bidding: {
    model: "cpc" | "cpm";
    bid_amount: number;
    max_bid: number;
  };
  targeting: {
    categories: string[];
    keywords: string[];
    min_semantic_score: number;
    product_ids: string[]; // Selected product IDs
    all_products: boolean; // If true, promote all products
  };
  performance: {
    impressions: number;
    clicks: number;
    conversions: number;
    ctr: number;
  };
}

// Product from PIC catalog
interface Product {
  id: string;
  sku: string;
  title: string;
  brand: string;
  category: string;
  price: number;
  currency: string;
  image_url: string;
  status: string;
}

// Use internal campaigns API
const CAMPAIGNS_API = "/api/v1/campaigns";

// Icons
const PlusIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M10 4v12M4 10h12" strokeLinecap="round" />
  </svg>
);

const AdIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <rect x="2" y="3" width="16" height="14" rx="2" />
    <path d="M6 8h8M6 11h5" strokeLinecap="round" />
    <circle cx="14" cy="11" r="1.5" fill="currentColor" />
  </svg>
);

const TrendUpIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M4 14l4-4 3 3 5-5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 8h4v4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const XIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M5 5l10 10M15 5l-10 10" strokeLinecap="round" />
  </svg>
);

// Format currency
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount);
}

// Format large numbers
function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}

// Status badge - Pentatonic colors
function StatusBadge({ status }: { status: SponsoredCampaign["status"] }) {
  const config = {
    active: { bg: "#00FBA915", color: "#00C48C", label: "Active" },
    paused: { bg: "#FFF8E1", color: "#A3A3A3", label: "Paused" },
    deleted: { bg: "#FEE2E2", color: "#A3A3A3", label: "Deleted" },
  };
  const { bg, color, label } = config[status];
  return (
    <span
      className="px-2 py-1 rounded text-xs font-light"
      style={{ backgroundColor: bg, color }}
    >
      {label}
    </span>
  );
}

// Create Campaign Modal
function CreateCampaignModal({
  isOpen,
  onClose,
  onSubmit,
  brandName,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  brandName: string;
}) {
  const [step, setStep] = useState(1); // 1 = Select Products, 2 = Campaign Details
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(
    new Set(),
  );
  const [selectAll, setSelectAll] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    daily_budget: 100,
    monthly_budget: 2000,
    bid_amount: 0.4,
  });

  // Fetch products when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchProducts();
    }
  }, [isOpen]);

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      // Fetch all products from PIC catalog
      const response = await tenantFetch("/api/products?limit=100");
      console.log(
        "[Sponsored] Products API response:",
        response.status,
        response.statusText,
      );

      if (response.ok) {
        const data = await response.json();
        console.log("[Sponsored] Products data:", data);
        setProducts(data.products || []);
      } else {
        const errorText = await response.text();
        console.error("[Sponsored] Products API error:", errorText);
        setProducts([]);
      }
    } catch (error) {
      console.error("[Sponsored] Failed to fetch products:", error);
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const toggleProduct = (productId: string) => {
    const newSelected = new Set(selectedProductIds);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProductIds(newSelected);
    setSelectAll(false);
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectAll(false);
      setSelectedProductIds(new Set());
    } else {
      setSelectAll(true);
      setSelectedProductIds(new Set());
    }
  };

  const filteredProducts = products.filter(
    (p) =>
      p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.category?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const selectedCount = selectAll ? products.length : selectedProductIds.size;

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      brand_id: brandName.toLowerCase().replace(/[^a-z0-9]/g, ""),
      brand_name: brandName,
      name: formData.name,
      daily_budget: formData.daily_budget,
      monthly_budget: formData.monthly_budget,
      bid_amount: formData.bid_amount,
      all_products: selectAll,
      product_ids: selectAll ? [] : Array.from(selectedProductIds),
    });
    onClose();
    // Reset state
    setStep(1);
    setSelectAll(true);
    setSelectedProductIds(new Set());
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div
          className="flex items-center justify-between p-6 border-b"
          style={{ borderColor: "rgba(169,169,169,0.1)" }}
        >
          <div>
            <h2 className="text-lg font-light" style={{ color: "#171717" }}>
              Create Sponsored Campaign
            </h2>
            <p className="text-sm" style={{ color: "#737373" }}>
              Step {step} of 2:{" "}
              {step === 1 ? "Select Products" : "Campaign Details"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <XIcon />
          </button>
        </div>

        {/* Step 1: Product Selection */}
        {step === 1 && (
          <div className="flex flex-col flex-1 overflow-hidden">
            <div
              className="p-4 border-b"
              style={{ borderColor: "rgba(169,169,169,0.1)" }}
            >
              {/* Select All / Search */}
              <div className="flex items-center gap-4 mb-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <span
                    className="text-sm font-medium"
                    style={{ color: "#171717" }}
                  >
                    Promote All Products ({products.length})
                  </span>
                </label>
              </div>

              {!selectAll && (
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border text-sm"
                  style={{ borderColor: "rgba(169,169,169,0.3)" }}
                />
              )}
            </div>

            {/* Product List */}
            <div
              className="flex-1 overflow-y-auto p-4"
              style={{ maxHeight: "400px" }}
            >
              {loadingProducts ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-gray-600" />
                </div>
              ) : selectAll ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-2">✓</div>
                  <p
                    className="text-sm font-medium"
                    style={{ color: "#171717" }}
                  >
                    All {products.length} products selected
                  </p>
                  <p className="text-xs mt-1" style={{ color: "#737373" }}>
                    Uncheck "Promote All Products" to select specific items
                  </p>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm" style={{ color: "#737373" }}>
                    No products found
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredProducts.map((product) => (
                    <label
                      key={product.id}
                      className="flex items-center gap-3 p-3 border cursor-pointer hover:bg-gray-50 transition-colors"
                      style={{
                        borderColor: selectedProductIds.has(product.id)
                          ? "#171717"
                          : "rgba(169,169,169,0.2)",
                        backgroundColor: selectedProductIds.has(product.id)
                          ? "#FAFAFA"
                          : "white",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedProductIds.has(product.id)}
                        onChange={() => toggleProduct(product.id)}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product.title}
                          className="w-12 h-12 object-cover rounded"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                          <span
                            className="text-xs"
                            style={{ color: "#737373" }}
                          >
                            No img
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p
                          className="text-sm font-medium truncate"
                          style={{ color: "#171717" }}
                        >
                          {product.title}
                        </p>
                        <p className="text-xs" style={{ color: "#737373" }}>
                          {product.sku && `SKU: ${product.sku} • `}
                          {product.category && `${product.category} • `}
                          {product.price &&
                            `${product.currency || "$"}${product.price}`}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div
              className="p-4 border-t flex items-center justify-between"
              style={{ borderColor: "rgba(169,169,169,0.1)" }}
            >
              <p className="text-sm" style={{ color: "#737373" }}>
                {selectedCount} product{selectedCount !== 1 ? "s" : ""} selected
              </p>
              <button
                type="button"
                onClick={() => setStep(2)}
                disabled={selectedCount === 0}
                className="px-4 py-2 rounded-full text-sm font-medium text-white transition-colors disabled:opacity-50"
                style={{ backgroundColor: "#171717" }}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Campaign Details */}
        {step === 2 && (
          <form
            onSubmit={handleSubmit}
            className="p-6 space-y-4 overflow-y-auto"
          >
            <div className="bg-green-50 p-3 mb-4">
              <p className="text-sm text-green-800">
                <strong>
                  {selectedCount} product{selectedCount !== 1 ? "s" : ""}
                </strong>{" "}
                will be promoted in this campaign
              </p>
            </div>

            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: "#171717" }}
              >
                Campaign Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full px-3 py-2 border text-sm"
                style={{ borderColor: "rgba(169,169,169,0.3)" }}
                placeholder="e.g., Summer Collection Push"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: "#171717" }}
                >
                  Daily Budget ($)
                </label>
                <input
                  type="number"
                  value={formData.daily_budget}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      daily_budget: parseFloat(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border text-sm"
                  style={{ borderColor: "rgba(169,169,169,0.3)" }}
                  min="10"
                  step="10"
                />
              </div>
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: "#171717" }}
                >
                  Monthly Budget ($)
                </label>
                <input
                  type="number"
                  value={formData.monthly_budget}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      monthly_budget: parseFloat(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border text-sm"
                  style={{ borderColor: "rgba(169,169,169,0.3)" }}
                  min="100"
                  step="100"
                />
              </div>
            </div>

            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: "#171717" }}
              >
                Bid per Click ($)
              </label>
              <input
                type="number"
                value={formData.bid_amount}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    bid_amount: parseFloat(e.target.value),
                  })
                }
                className="w-full px-3 py-2 border text-sm"
                style={{ borderColor: "rgba(169,169,169,0.3)" }}
                min="0.01"
                max="10.00"
                step="0.01"
              />
              <p className="text-xs mt-1" style={{ color: "#737373" }}>
                Recommended: $0.30 - $0.60 for competitive visibility
              </p>
            </div>
            <div className="bg-blue-50 p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">
                ✓ Semantic Relevance Guarantee
              </h4>
              <p className="text-xs text-blue-700">
                Your products will only be promoted when they genuinely match
                what users are searching for. Minimum semantic score: 70%
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="flex-1 px-4 py-2 border text-sm font-medium hover:bg-gray-50 transition-colors"
                style={{
                  borderColor: "rgba(169,169,169,0.3)",
                  color: "#171717",
                }}
              >
                Back
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 rounded-full text-sm font-medium text-white transition-colors"
                style={{ backgroundColor: "#171717" }}
              >
                Create Campaign
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// Main component
function SponsoredAdsPage() {
  const { currentTenant } = useTenant();
  const [campaigns, setCampaigns] = useState<SponsoredCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCampaigns();
  }, [currentTenant.id]);

  const fetchCampaigns = async () => {
    setLoading(true);
    setError(null);
    try {
      // Try to fetch from DGX Ads API
      const response = await fetch(
        `${CAMPAIGNS_API}/campaigns?brand_id=${currentTenant.id}`,
      );
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data.campaigns || []);
      } else {
        // Use demo data
        setCampaigns(getDemoCampaigns(currentTenant.id));
      }
    } catch {
      // Use demo data on error
      setCampaigns(getDemoCampaigns(currentTenant.id));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async (data: any) => {
    try {
      const response = await fetch(`${CAMPAIGNS_API}/campaigns`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const result = await response.json();
        setCampaigns((prev) => [result.campaign, ...prev]);
      } else {
        // Add demo campaign locally
        const newCampaign: SponsoredCampaign = {
          _key: `${data.brand_id}_${Date.now()}`,
          ...data,
          status: "active",
          created_at: new Date().toISOString(),
          budget: {
            daily_limit: data.daily_budget,
            monthly_limit: data.monthly_budget,
            spent_today: 0,
            spent_month: 0,
            total_spent: 0,
          },
          bidding: {
            model: "cpc",
            bid_amount: data.bid_amount,
            max_bid: data.bid_amount * 2,
          },
          targeting: {
            categories: data.categories,
            keywords: data.keywords,
            min_semantic_score: 0.7,
          },
          performance: {
            impressions: 0,
            clicks: 0,
            conversions: 0,
            ctr: 0,
          },
        };
        setCampaigns((prev) => [newCampaign, ...prev]);
      }
    } catch {
      console.error("Failed to create campaign");
    }
  };

  const handlePause = async (campaignKey: string) => {
    setCampaigns((prev) =>
      prev.map((c) =>
        c._key === campaignKey ? { ...c, status: "paused" as const } : c,
      ),
    );
  };

  const handleResume = async (campaignKey: string) => {
    setCampaigns((prev) =>
      prev.map((c) =>
        c._key === campaignKey ? { ...c, status: "active" as const } : c,
      ),
    );
  };

  // Calculate totals
  const totalSpend = campaigns.reduce(
    (sum, c) => sum + c.budget.total_spent,
    0,
  );
  const totalImpressions = campaigns.reduce(
    (sum, c) => sum + c.performance.impressions,
    0,
  );
  const totalClicks = campaigns.reduce(
    (sum, c) => sum + c.performance.clicks,
    0,
  );
  const avgCtr =
    totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
  const activeCampaigns = campaigns.filter((c) => c.status === "active").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{
            borderColor: currentTenant.color,
            borderTopColor: "transparent",
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light" style={{ color: "#171717" }}>
            Sponsored Products
          </h1>
          <p className="text-sm mt-1" style={{ color: "#737373" }}>
            Boost your products in Pentatonic Marketplace search results
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/campaign-analytics"
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-light transition-colors hover:bg-gray-50"
            style={{ border: "1px solid #E5E5E5", color: "#525252" }}
          >
            <TrendUpIcon />
            Analytics
          </Link>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-light transition-opacity hover:opacity-90"
            style={{ backgroundColor: currentTenant.color }}
          >
            <PlusIcon />
            New Campaign
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        <div
          className="p-5"
          style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
        >
          <p className="text-sm font-light mb-2" style={{ color: "#737373" }}>
            Active Campaigns
          </p>
          <p className="text-2xl font-light" style={{ color: "#171717" }}>
            {activeCampaigns}
          </p>
        </div>
        <div
          className="p-5"
          style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
        >
          <p className="text-sm font-light mb-2" style={{ color: "#737373" }}>
            Total Spend
          </p>
          <p className="text-2xl font-light" style={{ color: "#171717" }}>
            {formatCurrency(totalSpend)}
          </p>
        </div>
        <div
          className="p-5"
          style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
        >
          <p className="text-sm font-light mb-2" style={{ color: "#737373" }}>
            Impressions
          </p>
          <p className="text-2xl font-light" style={{ color: "#171717" }}>
            {formatNumber(totalImpressions)}
          </p>
        </div>
        <div
          className="p-5"
          style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
        >
          <p className="text-sm font-light mb-2" style={{ color: "#737373" }}>
            Clicks
          </p>
          <p className="text-2xl font-light" style={{ color: "#171717" }}>
            {formatNumber(totalClicks)}
          </p>
        </div>
        <div
          className="p-5"
          style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
        >
          <p className="text-sm font-light mb-2" style={{ color: "#737373" }}>
            Avg CTR
          </p>
          <p
            className="text-2xl font-light"
            style={{ color: currentTenant.color }}
          >
            {avgCtr.toFixed(2)}%
          </p>
        </div>
      </div>

      {/* Campaigns List */}
      <div style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}>
        <div className="p-4" style={{ borderBottom: "1px solid #E5E5E5" }}>
          <h2 className="font-light" style={{ color: "#171717" }}>
            All Campaigns
          </h2>
        </div>

        {campaigns.length === 0 ? (
          <div className="p-12 text-center">
            <AdIcon />
            <h3 className="mt-4 font-light" style={{ color: "#171717" }}>
              No sponsored campaigns yet
            </h3>
            <p className="text-sm mt-1 font-light" style={{ color: "#737373" }}>
              Create your first campaign to boost your products in search
              results
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 px-4 py-2 rounded-full text-white text-sm font-light transition-opacity hover:opacity-90"
              style={{ backgroundColor: currentTenant.color }}
            >
              Create Campaign
            </button>
          </div>
        ) : (
          <div>
            {campaigns.map((campaign) => (
              <div
                key={campaign._key}
                className="p-4 hover:bg-white transition-colors"
                style={{ borderBottom: "1px solid #E5E5E5" }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Link
                        to="/sponsored/$id"
                        params={{ id: campaign._key }}
                        className="font-light hover:underline"
                        style={{ color: "#171717" }}
                      >
                        {campaign.name}
                      </Link>
                      <StatusBadge status={campaign.status} />
                    </div>

                    <div className="flex items-center gap-6 text-sm mb-2 font-light">
                      <div>
                        <span style={{ color: "#737373" }}>Bid: </span>
                        <span style={{ color: "#171717" }}>
                          {formatCurrency(campaign.bidding.bid_amount)}/click
                        </span>
                      </div>
                      <div>
                        <span style={{ color: "#737373" }}>Daily Budget: </span>
                        <span style={{ color: "#171717" }}>
                          {formatCurrency(campaign.budget.spent_today)} /{" "}
                          {formatCurrency(campaign.budget.daily_limit)}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: "#737373" }}>Monthly: </span>
                        <span style={{ color: "#171717" }}>
                          {formatCurrency(campaign.budget.spent_month)} /{" "}
                          {formatCurrency(campaign.budget.monthly_limit)}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-6 text-sm font-light">
                      <div>
                        <span style={{ color: "#737373" }}>Impressions: </span>
                        <span style={{ color: "#171717" }}>
                          {formatNumber(campaign.performance.impressions)}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: "#737373" }}>Clicks: </span>
                        <span style={{ color: "#171717" }}>
                          {formatNumber(campaign.performance.clicks)}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: "#737373" }}>CTR: </span>
                        <span style={{ color: "#171717" }}>
                          {campaign.performance.impressions > 0
                            ? (
                                (campaign.performance.clicks /
                                  campaign.performance.impressions) *
                                100
                              ).toFixed(2)
                            : "0.00"}
                          %
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {campaign.status === "active" ? (
                      <button
                        onClick={() => handlePause(campaign._key)}
                        className="px-3 py-1.5 rounded-full text-sm border transition-colors hover:bg-gray-50"
                        style={{
                          borderColor: "rgba(169,169,169,0.3)",
                          color: "#E65100",
                        }}
                      >
                        Pause
                      </button>
                    ) : (
                      campaign.status === "paused" && (
                        <button
                          onClick={() => handleResume(campaign._key)}
                          className="px-3 py-1.5 rounded-full text-sm text-white transition-colors hover:opacity-90"
                          style={{ backgroundColor: "#2E7D32" }}
                        >
                          Resume
                        </button>
                      )
                    )}
                  </div>
                </div>

                {/* Budget progress bar */}
                <div className="mt-3">
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full transition-all duration-300"
                      style={{
                        width: `${Math.min(100, (campaign.budget.spent_today / campaign.budget.daily_limit) * 100)}%`,
                        backgroundColor:
                          campaign.budget.spent_today >=
                          campaign.budget.daily_limit * 0.9
                            ? "#E65100"
                            : "#00A693",
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      <CreateCampaignModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateCampaign}
        brandName={currentTenant.name || currentTenant.id}
      />

      {error && (
        <div className="fixed bottom-4 right-4 bg-red-50 border border-red-200 p-4 text-sm text-red-700">
          {error}
        </div>
      )}
    </div>
  );
}

// Demo data generator
function getDemoCampaigns(tenantId: string): SponsoredCampaign[] {
  // Generate realistic demo data based on tenant
  const baseCampaigns: SponsoredCampaign[] = [
    {
      _key: `${tenantId}_summer_2026`,
      brand_id: tenantId,
      brand_name: tenantId.charAt(0).toUpperCase() + tenantId.slice(1),
      name: "Summer Collection Push",
      status: "active",
      created_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
      budget: {
        daily_limit: 500,
        monthly_limit: 10000,
        spent_today: 127.45,
        spent_month: 3241.8,
        total_spent: 8456.23,
      },
      bidding: {
        model: "cpc",
        bid_amount: 0.45,
        max_bid: 1.0,
      },
      targeting: {
        categories: ["sneakers", "running shoes", "athletic footwear"],
        keywords: ["running", "marathon", "performance"],
        min_semantic_score: 0.7,
        product_ids: [],
        all_products: true,
      },
      performance: {
        impressions: 45230,
        clicks: 1847,
        conversions: 89,
        ctr: 4.1,
      },
    },
    {
      _key: `${tenantId}_brand_awareness`,
      brand_id: tenantId,
      brand_name: tenantId.charAt(0).toUpperCase() + tenantId.slice(1),
      name: "Brand Awareness Q1",
      status: "active",
      created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      budget: {
        daily_limit: 300,
        monthly_limit: 6000,
        spent_today: 89.2,
        spent_month: 2145.6,
        total_spent: 5678.9,
      },
      bidding: {
        model: "cpc",
        bid_amount: 0.38,
        max_bid: 0.75,
      },
      targeting: {
        categories: ["apparel", "activewear", "sportswear"],
        keywords: ["fitness", "workout", "training"],
        min_semantic_score: 0.7,
        product_ids: [],
        all_products: true,
      },
      performance: {
        impressions: 32150,
        clicks: 1287,
        conversions: 56,
        ctr: 4.0,
      },
    },
    {
      _key: `${tenantId}_holiday_paused`,
      brand_id: tenantId,
      brand_name: tenantId.charAt(0).toUpperCase() + tenantId.slice(1),
      name: "Holiday Special (Paused)",
      status: "paused",
      created_at: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
      budget: {
        daily_limit: 200,
        monthly_limit: 4000,
        spent_today: 0,
        spent_month: 0,
        total_spent: 2345.67,
      },
      bidding: {
        model: "cpc",
        bid_amount: 0.35,
        max_bid: 0.7,
      },
      targeting: {
        categories: ["gifts", "accessories"],
        keywords: ["holiday", "gift", "present"],
        min_semantic_score: 0.7,
        product_ids: [],
        all_products: true,
      },
      performance: {
        impressions: 18500,
        clicks: 648,
        conversions: 34,
        ctr: 3.5,
      },
    },
  ];

  return baseCampaigns;
}

export const Route = createFileRoute("/sponsored")({
  component: SponsoredAdsPage,
});
